// How a rule starts at the table (qa-runs#371; docs/MRCS/workbench.md §6). An entry's block may list its `starts`
// (validated by `scripts/mrcs/blocks.js` `checkStarts`): each `{ how }` is
//   used       someone uses it (the dice bag, the hot bar, the chain runner's Use);
//   reaction   the table starts it when one of the closed list of table events happens (`when`, triggers.ts), its
//              `roles` naming, for each of the event's roles, the rule's role it fills (absent: the same key);
//   always     it applies all the time while the creature has it;
//   toggle     the player switches it on and it stays on until switched off, the encounter ends, or `rounds` rounds
//              pass (`ends`), each switch spending `action` (free, swift, move, standard; none: no action).
// An entry without `starts` reads its starts from the old fields, unchanged: `when` a Reaction, `standing` Always on,
// neither Used. A rule may have several, and the table starts it from any of them; the log line says which
// ("Attack roll, as a reaction: Kara provoked").
//
// TOGGLES are table state, per token (`Toggles`): each one lit, with the round it was switched on in (null outside an
// encounter). `toggle.on` and `toggle.off` are events; a toggle that ends by itself is switched off by the server's
// event (`togglesEnding`), its reason recorded. A toggle is as visible as its token: a player is told the toggles of
// the tokens they may see (`projectToggles`), names only.
import { TRIGGERS, isTriggerKey } from './triggers.js';
export const START_HOWS = ['used', 'reaction', 'always', 'toggle'];
export const TOGGLE_ENDS = ['off', 'encounter', 'rounds'];
export const TOGGLE_ACTIONS = ['free', 'swift', 'move', 'standard', 'none'];
export const START_WORDS = { used: 'Used', reaction: 'Reaction', always: 'Always on', toggle: 'Toggle' };
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const KEY_RE = /^[a-z][a-z0-9_]{0,31}$/;
/** One start read strictly (the validator's shape), or null. */
export function parseStart(v) {
    if (!isObj(v))
        return null;
    switch (v.how) {
        case 'used': return TOGGLE_ACTIONS.includes(v.action) ? { how: 'used', action: v.action } : { how: 'used' };
        case 'always': return { how: 'always' };
        case 'reaction': {
            if (!isTriggerKey(v.when))
                return null;
            if (v.roles === undefined)
                return { how: 'reaction', when: v.when };
            if (!isObj(v.roles))
                return null;
            const roles = {};
            for (const [k, r] of Object.entries(v.roles)) {
                if (!TRIGGERS[v.when].roles.includes(k) || typeof r !== 'string' || !KEY_RE.test(r))
                    return null;
                roles[k] = r;
            }
            return { how: 'reaction', when: v.when, roles };
        }
        case 'toggle': {
            if (!TOGGLE_ENDS.includes(v.ends) || !TOGGLE_ACTIONS.includes(v.action))
                return null;
            const ends = v.ends;
            const action = v.action;
            if (ends === 'rounds')
                return typeof v.rounds === 'number' && Number.isInteger(v.rounds) && v.rounds >= 1 && v.rounds <= 100 ? { how: 'toggle', ends, rounds: v.rounds, action } : null;
            return { how: 'toggle', ends, action };
        }
        default: return null;
    }
}
/** An entry's starts: its listed ones (a malformed one dropped), else read from the old fields with no change in behaviour. */
export function startsOf(block) {
    if (Array.isArray(block.starts)) {
        const out = block.starts.slice(0, 8).map(parseStart).filter((s) => s !== null);
        if (out.length)
            return out;
    }
    if (isTriggerKey(block.when))
        return [{ how: 'reaction', when: block.when }, ...(block.standing === true ? [{ how: 'always' }] : [])];
    if (block.standing === true)
        return [{ how: 'always' }];
    return [{ how: 'used' }];
}
/** The Reaction start an entry answers a table event with, or null: the one test for "does this entry start from it". */
export function reactionTo(block, trigger) {
    return startsOf(block).find((s) => s.how === 'reaction' && s.when === trigger) ?? null;
}
/** Whether an entry starts from any table event (the server's When… list). */
export const reactsAtAll = (block) => startsOf(block).some((s) => s.how === 'reaction');
/** A class feature's Used start, or null: the hot bar's tap runs its chain (feats.md §10c). */
export const featureUseStart = (block) => (block.class_feature === undefined ? null : startsOf(block).find((s) => s.how === 'used') ?? null);
/** An entry's Toggle start, or null. */
export const toggleStart = (block) => startsOf(block).find((s) => s.how === 'toggle') ?? null;
/**
 * The event's creatures renamed to the rule's roles by a Reaction start's `roles` (absent: same key), for `triggerRoles`:
 * pass the result as the event's roles. The carrier's role is kept under its own key too.
 */
export function mappedEvent(event, start) {
    if (!start.roles)
        return event;
    const roles = { ...event.roles };
    for (const [from, to] of Object.entries(start.roles))
        if (event.roles[from] !== undefined)
            roles[to] = event.roles[from];
    return { ...event, roles };
}
/** What happened, in the past tense, with the creatures' names (`name` gives a name the viewer may be told, or null). */
const PAST = {
    used: (n) => `${n('user')} used it`,
    provokes: (n) => `${n('provoker')} provoked`,
    concentration_fails: (n) => `${n('caster')} lost their concentration`,
    hit_melee: (n) => `${n('attacker')} hit ${n('hit')} in melee`,
    hit_ranged: (n) => `${n('attacker')} hit ${n('hit')} at range`,
    turn_start: (n) => `${n('creature')}'s turn started`,
    turn_end: (n) => `${n('creature')}'s turn ended`,
    drops: (n) => `${n('creature')} dropped`,
    damaged: (n) => `${n('creature')} took damage`,
    attacks: (n) => `${n('attacker')} attacked ${n('target')}`,
    attacked: (n) => `${n('target')} was attacked by ${n('attacker')}`,
    fells: (n) => `${n('attacker')} felled ${n('felled')}`,
    hits: (n) => `${n('attacker')} hit ${n('target')}`,
    hits_any: (n) => `${n('attacker')} hit ${n('target')}`,
    misses: (n) => `${n('attacker')} missed ${n('target')}`,
    crits: (n) => `${n('attacker')} scored a critical hit on ${n('target')}`,
    mount_hit: (n) => `${n('attacker')} hit ${n('hit')}, ridden by ${n('rider')}`,
    fails_save: (n) => `${n('creature')} failed a save`,
};
/**
 * The log line of a rule started at the table, naming the start it came from: "Attack roll, as a reaction: Kara
 * provoked", "Rage, switched on", "Power Attack, used". `name(id)` is the creature's name as the viewer may know it.
 */
export function startLine(entryName, start, event, name = () => 'a creature') {
    switch (start.how) {
        case 'used': return `${entryName}, used`;
        case 'always': return `${entryName}, always on`;
        case 'toggle': return `${entryName}, switched on`;
        case 'reaction': {
            if (!event)
                return `${entryName}, as a reaction`;
            const n = (role) => { const id = event.roles[role]; return id ? name(id) : 'a creature'; };
            return `${entryName}, as a reaction: ${PAST[start.when](n)}`;
        }
    }
}
export const TOGGLE_LIMITS = { perToken: 16, name: 120 };
export const TOGGLE_KINDS = ['toggle.on', 'toggle.off'];
const ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ENTRY_RE = /^[a-z][a-z0-9_]{0,63}$/;
/** A toggle event from the wire or the log, strict. `since` and the start's fields on `toggle.on` are the server's stamp. */
export function parseToggleBody(b) {
    if (typeof b.token !== 'string' || !ID_RE.test(b.token))
        return null;
    if (b.kind === 'toggle.off') {
        if (typeof b.entry !== 'string' || !ENTRY_RE.test(b.entry))
            return null;
        const why = b.why === undefined ? 'off' : b.why;
        return TOGGLE_ENDS.includes(why) ? { kind: 'toggle.off', token: b.token, entry: b.entry, why: why } : null;
    }
    if (b.kind !== 'toggle.on' || !isObj(b.entry))
        return null;
    const e = b.entry;
    if (typeof e.id !== 'string' || !ENTRY_RE.test(e.id) || typeof e.version !== 'number' || !Number.isInteger(e.version) || typeof e.name !== 'string' || !e.name.trim() || e.name.length > TOGGLE_LIMITS.name)
        return null;
    const start = parseStart({ how: 'toggle', ends: b.ends, rounds: b.rounds, action: b.action });
    if (!start || start.how !== 'toggle')
        return null;
    const since = b.since === null || b.since === undefined ? null : typeof b.since === 'number' && Number.isInteger(b.since) && b.since >= 0 ? b.since : undefined;
    if (since === undefined)
        return null;
    return { kind: 'toggle.on', token: b.token, entry: { id: e.id, version: e.version, name: e.name }, ends: start.ends, ...(start.rounds ? { rounds: start.rounds } : {}), action: start.action, since };
}
/** Whether a token has an entry's toggle lit. */
export const isLit = (t, token, entry) => (t?.[token] ?? []).some((x) => x.entry.id === entry);
/** Why a switch is refused, or null. The caller has checked the actor may act for the token (its owner, or the DM). */
export function toggleRefusal(t, body) {
    const lit = isLit(t, body.token, body.kind === 'toggle.on' ? body.entry.id : body.entry);
    if (body.kind === 'toggle.on') {
        if (lit)
            return `${body.entry.name} is already on.`;
        if ((t?.[body.token]?.length ?? 0) >= TOGGLE_LIMITS.perToken)
            return 'That token has as many toggles on as it can hold.';
        return null;
    }
    return lit ? null : 'That toggle is not on.';
}
/** Apply an accepted toggle event. */
export function reduceToggles(t, body) {
    const out = { ...(t ?? {}) };
    const mine = out[body.token] ?? [];
    if (body.kind === 'toggle.on') {
        out[body.token] = [...mine.filter((x) => x.entry.id !== body.entry.id), { entry: { ...body.entry }, ends: body.ends, ...(body.rounds ? { rounds: body.rounds } : {}), action: body.action, since: body.since }];
    }
    else {
        const kept = mine.filter((x) => x.entry.id !== body.entry);
        if (kept.length)
            out[body.token] = kept;
        else
            delete out[body.token];
    }
    return out;
}
/** A token gone from the scene takes its toggles with it. */
export function dropTokenToggles(t, token) {
    if (!t?.[token])
        return t ?? {};
    const out = { ...t };
    delete out[token];
    return out;
}
/**
 * The toggles that end by themselves between two encounter states (the server commits a `toggle.off` for each): every
 * toggle that ends with the encounter when it stops running, and one that lasts N rounds once N rounds have passed
 * since it was switched on (at the start of round since + N). A rounds toggle switched on outside an encounter lasts
 * until switched off.
 */
export function togglesEnding(t, before, after) {
    const out = [];
    for (const [token, list] of Object.entries(t ?? {}))
        for (const x of list) {
            if (before.running && !after.running && (x.ends === 'encounter' || (x.ends === 'rounds' && x.since !== null)))
                out.push({ kind: 'toggle.off', token, entry: x.entry.id, why: x.ends });
            else if (after.running && x.ends === 'rounds' && x.since !== null && x.rounds && after.round >= x.since + x.rounds)
                out.push({ kind: 'toggle.off', token, entry: x.entry.id, why: 'rounds' });
        }
    return out;
}
/** The turn action a switch spends, as the system's action kind, or null (none). */
export const toggleSpend = (action) => (action === 'none' ? null : action);
/** The log line of a switch. */
export function toggleLine(body, tokenName, entryName) {
    if (body.kind === 'toggle.on') {
        const lasts = body.ends === 'encounter' ? ' until the end of the encounter' : body.ends === 'rounds' ? ` for ${body.rounds} round${body.rounds === 1 ? '' : 's'}` : '';
        return `${tokenName}: ${startLine(body.entry.name, { how: 'toggle', ends: body.ends, action: body.action })}${lasts}${body.action !== 'none' ? ` (a ${body.action} action)` : ''}`;
    }
    const name = entryName ?? body.entry;
    return `${tokenName}: ${name}, switched off${body.why === 'encounter' ? ' as the encounter ended' : body.why === 'rounds' ? ', its rounds over' : ''}`;
}
/** The toggles a viewer is told of: the DM all; a player those on tokens they may see, the entry's name and how it ends. */
export function projectToggles(t, role, sees) {
    if (!t || !Object.keys(t).length)
        return undefined;
    const out = {};
    for (const [token, list] of Object.entries(t)) {
        if (role !== 'dm' && !sees(token))
            continue;
        out[token] = list.map((x) => ({ ...x, entry: { ...x.entry } }));
    }
    return Object.keys(out).length ? out : undefined;
}
/** A token's lit toggles' names, for the hot bar and the token's ring. */
export const litNames = (t, token) => (t?.[token] ?? []).map((x) => x.entry.name);
// ── The hot bar's Toggle boxes ──────────────────────────────────────────────────────────────────────────────────────
/**
 * The wire event a client asks a token's toggle rules by (qa-runs#371): `{ campaignId, token }`, answered
 * `{ ok: true, toggles: ToggleChoice[] }` — the campaign's entries with a Toggle start that the token carries — or
 * `{ ok: false, error }`. Asked of a token of the asker's (the DM: any) that they may see.
 */
export const TABLE_TOGGLES = 'table:toggles';
export const TOGGLE_CHOICES_MAX = 40;
/** The answer to `TABLE_TOGGLES`, read strictly: anything out of shape is left out; null when it is not an answer. */
export function parseToggleChoices(v) {
    if (!isObj(v) || v.ok !== true || !Array.isArray(v.toggles))
        return null;
    const out = [];
    for (const x of v.toggles.slice(0, TOGGLE_CHOICES_MAX)) {
        if (!isObj(x) || typeof x.id !== 'string' || !ENTRY_RE.test(x.id) || typeof x.name !== 'string' || !x.name.trim() || x.name.length > TOGGLE_LIMITS.name)
            continue;
        const start = parseStart({ how: 'toggle', ends: x.ends, rounds: x.rounds, action: x.action });
        if (!start || start.how !== 'toggle')
            continue;
        out.push({ id: x.id, name: x.name, ends: start.ends, ...(start.rounds ? { rounds: start.rounds } : {}), action: start.action, ...(x.use === true ? { use: true } : {}) });
    }
    return out;
}
/** How a toggle lasts and what a switch costs, in words: "until switched off · a free action". */
export function toggleWords(t) {
    if (t.use)
        return t.action === 'none' ? 'used at a tap' : `used at a tap · a ${t.action} action`;
    const lasts = t.ends === 'encounter' ? 'until the end of the encounter' : t.ends === 'rounds' ? `for ${t.rounds} round${t.rounds === 1 ? '' : 's'}` : 'until switched off';
    return t.action === 'none' ? lasts : `${lasts} · a ${t.action} action`;
}
//# sourceMappingURL=starts.js.map